import { useTranslation } from "react-i18next";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ArrowUturnLeftIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckCircleIcon,
  ClockIcon,
  CreditCardIcon,
  ReceiptPercentIcon,
  StarIcon,
  TagIcon,
  UserGroupIcon,
  UserMinusIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";

import { DASH, figureDir, fmtLocale, formatDenominator, formatKpiValue, formatPercent } from "./reportFormat";

const ICONS = {
  collections: BanknotesIcon,
  net_collections: BanknotesIcon,
  ex_tax_net_sales: ReceiptPercentIcon,
  booked_value: CalendarDaysIcon,
  receivables: CreditCardIcon,
  refunds: ArrowUturnLeftIcon,
  processor_fees: CreditCardIcon,
  average_payment: TagIcon,
  average_ticket: TagIcon,
  completed_visits: CalendarDaysIcon,
  completion_rate: CheckCircleIcon,
  no_show_rate: UserMinusIcon,
  cancellation_rate: XCircleIcon,
  new_clients: UserGroupIcon,
  first_visit_clients: UserGroupIcon,
  active_clients: UserGroupIcon,
  booked_utilization: ClockIcon,
  delivered_utilization: ClockIcon,
  average_rating: StarIcon,
};

/**
 * The KPI row (spec 02 §4): icon, label, value, change against the comparison.
 *
 * Colour follows `higher_is_better`, not the arrow — more refunds or no-shows
 * is red even though it is "up". No comparison is "—"; a zero baseline is
 * "New" / "N/A" as the engine labelled it (decision 4). Money KPIs carry one
 * line per currency when the reader chose "All currencies" (decision 2).
 *
 * With `onSelect`, a tile opens the rows behind it (AC01): Enter / Space or a
 * click hands the KPI back to the page, which shows the matching table rows.
 */
export default function KpiTiles({ kpis = [], onSelect }) {
  if (kpis.length === 0) return null;

  return (
    <div
      className={`grid gap-3 sm:grid-cols-2 ${
        kpis.length >= 5 ? "xl:grid-cols-5" : kpis.length === 4 ? "xl:grid-cols-4" : "xl:grid-cols-3"
      }`}
    >
      {kpis.map((kpi) => (
        <KpiTile key={kpi.key} kpi={kpi} onSelect={onSelect} />
      ))}
    </div>
  );
}

function KpiTile({ kpi, onSelect }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const Icon = ICONS[kpi.key] ?? ChartBarIcon;

  const tone =
    kpi.higher_is_better === false ? "bg-danger-wash text-danger-text" : "bg-accent-wash text-accent-text";
  const values = kpi.values?.length
    ? kpi.values
    : [{ currency: null, current: null, comparison: null, unknown: true }];
  const isRate = kpi.type === "percent";

  return (
    <div
      className={`card group relative flex gap-3 p-4 ${onSelect ? "cursor-pointer hover:border-accent" : ""}`}
      tabIndex={0}
      {...(onSelect
        ? {
            role: "button",
            "aria-label": t("reports.shell.kpiShowRows", { label: kpi.label }),
            onClick: () => onSelect(kpi),
            onKeyDown: (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(kpi);
              }
            },
          }
        : {})}
    >
      <span className={`grid h-11 w-11 shrink-0 place-items-center rounded-full ${tone}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="truncate text-dense text-ink-2">{kpi.label}</p>

        {values.map((entry, index) => {
          const value =
            entry.unknown && !entry.current ? DASH : formatKpiValue(entry.current, kpi.type, locale, t);
          const denominator = isRate ? formatDenominator(entry.current, locale, t) : null;

          const shown = isRate && entry.current ? formatPercent(entry.current, locale, t) : value;

          return (
            <div key={entry.currency ?? index} className={index > 0 ? "mt-2 border-t border-line pt-2" : ""}>
              <p className="flex flex-wrap items-baseline gap-x-2">
                <span className={`font-bold ${values.length > 1 ? "text-lg" : "text-xl"}`} dir={figureDir(shown)}>
                  {shown}
                </span>
                {denominator && <span className="text-caption text-ink-2">{denominator}</span>}
                {/* An average always shows how many it is over (R38 ratings). */}
                {entry.count !== null && entry.count !== undefined && (
                  <span className="text-caption text-ink-2">
                    {t("reports.shell.countOf", { count: entry.count })}
                  </span>
                )}
                {entry.provisional && (
                  <span className="rounded-full bg-warning-wash px-2 py-0.5 text-caption text-warning-text">
                    {t("reports.shell.provisional")}
                  </span>
                )}
              </p>
              <Change comparison={entry.comparison} higherIsBetter={kpi.higher_is_better} isRate={isRate} />
            </div>
          );
        })}
      </div>

      {/* Formula + denominator + the comparison figure, on hover or focus. */}
      <div
        role="tooltip"
        className="pointer-events-none invisible absolute start-3 top-full z-20 mt-1 w-72 max-w-[calc(100%-1.5rem)] rounded-lg border border-line bg-surface p-3 text-caption text-ink-2 opacity-0 shadow-e2 transition-opacity duration-fast group-focus-within:visible group-focus-within:opacity-100 group-hover:visible group-hover:opacity-100"
      >
        <p className="font-semibold text-ink">{kpi.label}</p>
        <p className="mt-1">{kpi.formula}</p>
        {!kpi.available && kpi.unavailable_reason && <p className="mt-1">{kpi.unavailable_reason}</p>}
        {values.map((entry, index) =>
          entry.previous !== undefined && entry.previous !== null ? (
            <p key={entry.currency ?? index} className="mt-1" dir="auto">
              {t("reports.shell.previousValue", {
                value: formatKpiValue(entry.previous, kpi.type, locale, t),
              })}
              {isRate && formatDenominator(entry.previous, locale, t)
                ? ` · ${formatDenominator(entry.previous, locale, t)}`
                : ""}
            </p>
          ) : null
        )}
      </div>
    </div>
  );
}

function Change({ comparison, higherIsBetter, isRate }) {
  const { t, i18n } = useTranslation();

  if (!comparison) {
    return <p className="mt-1 text-dense text-ink-2">{DASH}</p>;
  }

  if (comparison.change_label === "new" || comparison.change_label === "n/a") {
    return (
      <p className="mt-1 text-dense font-semibold text-ink-2">
        {t(comparison.change_label === "new" ? "reports.change.new" : "reports.change.na")}
      </p>
    );
  }

  const change = isRate ? comparison.change_pp : comparison.change_pct;

  if (change === null || change === undefined) {
    return <p className="mt-1 text-dense text-ink-2">{DASH}</p>;
  }

  const direction = comparison.direction;
  const good = direction === "flat" ? null : (direction === "up") === (higherIsBetter !== false);
  const tone = good === null ? "text-ink-2" : good ? "text-accent-text" : "text-danger-text";
  const Arrow = direction === "up" ? ArrowUpIcon : direction === "down" ? ArrowDownIcon : null;
  const shown = new Intl.NumberFormat(fmtLocale(i18n.language), {
    maximumFractionDigits: 1,
    signDisplay: "exceptZero",
  }).format(change);

  return (
    <p className={`mt-1 flex items-center gap-1 text-dense font-semibold ${tone}`}>
      {Arrow && <Arrow className="h-3.5 w-3.5" aria-hidden="true" />}
      {/* "+2.5%" is a figure (LTR); "+2.5 نقطة مئوية" is a phrase (its own direction). */}
      <span dir={isRate ? "auto" : "ltr"}>{isRate ? t("reports.shell.pointsChange", { value: shown }) : `${shown}%`}</span>
    </p>
  );
}
